import { useEffect, useState } from 'react';
import { Copy, Link as LinkIcon, Trophy, Users } from 'lucide-react';
import { affiliateAPI, fmtError } from '@/lib/api';
import { formatInt, formatMoney } from '@/lib/format';

const TIER_COLORS = {
  bronze: '#a16207', silver: '#a1a1aa', gold: '#eab308', platinum: '#22d3ee', diamond: '#00ff66',
};

export default function Affiliate() {
  const [me, setMe] = useState(null);
  const [board, setBoard] = useState([]);
  const [requests, setRequests] = useState([]);
  const [err, setErr] = useState('');
  const [status, setStatus] = useState('');
  const [form, setForm] = useState({ amount: '', cryptocurrency: 'usdtbsc', wallet: '', network: 'BEP-20' });

  const load = async () => {
    try {
      const [m, lb, rq] = await Promise.all([affiliateAPI.me(), affiliateAPI.leaderboard(), affiliateAPI.myRequests()]);
      setMe(m); setBoard(lb); setRequests(rq);
    } catch (e) { setErr(fmtError(e)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setStatus('');
    try {
      await affiliateAPI.request({ ...form, amount: Number(form.amount) });
      setStatus('Affiliate payout request submitted.');
      setForm({ amount: '', cryptocurrency: 'usdtbsc', wallet: '', network: 'BEP-20' });
      load();
    } catch (e2) { setErr(fmtError(e2)); }
  };

  if (!me) return <div className="text-zinc-500 font-mono text-sm">LOADING AFFILIATE…</div>;

  const publicUrl = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
  const link = `${publicUrl}/register?ref=${me.referral_code}`;
  const copyLink = () => navigator.clipboard.writeText(link);

  return (
    <div className="space-y-6">
      <div>
        <div className="label-sm mb-2">Affiliate</div>
        <h1 className="font-display text-3xl">Grow with Traders Spring</h1>
        <p className="text-zinc-500 text-sm mt-1">Earn up to 25% commission on every trader you refer. Tiered rewards start at 5% and scale as you grow.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-tech rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="label-sm">Your referral link</div>
              <div className="font-mono text-sm mt-2 break-all" data-testid="ref-link">{link}</div>
            </div>
            <button onClick={copyLink} className="btn-primary btn-sm" data-testid="copy-ref"><Copy size={12} /> Copy</button>
          </div>

          {/* Prominent Total Invites hero — bigger + bolder than the standard KPIs. */}
          <div className="mt-6 relative overflow-hidden rounded-lg border border-[#00ff66]/30 p-5 sm:p-6"
               data-testid="total-invites-hero">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(0,255,102,0.14),transparent_60%)]" />
            <div className="relative flex items-center gap-5">
              <div className="w-14 h-14 rounded-full bg-[#00ff66]/10 border border-[#00ff66]/40 flex items-center justify-center shrink-0">
                <Users className="text-[#00ff66]" size={26} />
              </div>
              <div className="min-w-0">
                <div className="font-mono text-[10px] tracking-[3px] text-[#00ff66]">TOTAL INVITES</div>
                <div className="font-display text-5xl sm:text-6xl leading-none mt-1"
                     data-testid="total-invites-number">
                  {formatInt(me.referrals_count || 0)}
                </div>
                <div className="text-xs text-zinc-500 mt-2">
                  {(me.referrals_count || 0) === 0
                    ? 'No traders signed up with your code yet. Share your link to start earning.'
                    : (me.referrals_count === 1
                        ? '1 trader has signed up using your referral code so far.'
                        : `${formatInt(me.referrals_count)} traders have signed up using your referral code so far.`)}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-4">
            <Kpi k="Tier" v={me.affiliate_tier.toUpperCase()} color={TIER_COLORS[me.affiliate_tier] || '#fff'} />
            <Kpi k="Commission" v={`${me.affiliate_commission_rate}%`} />
            <Kpi k="Balance" v={`$${formatMoney(me.affiliate_balance)}`} />
          </div>
        </div>

        <div className="card-tech rounded-lg p-6">
          <div className="label-sm">Tiered rewards</div>
          <ul className="mt-3 space-y-2 text-sm">
            <Row t="Bronze" r="5%" note="Default" />
            <Row t="Silver" r="10%" note="$500+ earned" />
            <Row t="Gold" r="15%" note="$1,500+ earned" />
            <Row t="Platinum" r="20%" note="$5,000+ earned" />
            <Row t="Diamond" r="25%" note="$10,000+ earned" />
          </ul>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-tech rounded-lg p-6">
          <div className="label-sm">Request affiliate payout</div>
          <form onSubmit={submit} className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="aff-payout-form">
            <div>
              <div className="label-xs mb-1">Amount (USD)</div>
              <input className="input-tech" type="number" min="50" step="0.01" required value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="aff-amount" />
            </div>
            <div>
              <div className="label-xs mb-1">Cryptocurrency</div>
              <select className="input-tech" value={form.cryptocurrency} onChange={e => setForm({ ...form, cryptocurrency: e.target.value })} data-testid="aff-crypto">
                <option value="usdtbsc">USDT · BEP-20 (BSC)</option>
                <option value="usdcbsc">USDC · BEP-20 (BSC)</option>
                <option value="usdtmatic">USDT · Polygon</option>
                <option value="btc">BTC</option>
                <option value="eth">ETH</option>
                <option value="ltc">LTC</option>
                <option value="sol">SOL</option>
                <option value="trx">TRX</option>
              </select>
            </div>
            <div>
              <div className="label-xs mb-1">Network</div>
              <input className="input-tech" required value={form.network} onChange={e => setForm({ ...form, network: e.target.value })} placeholder="BEP-20 / ERC-20 / Polygon / …" data-testid="aff-network" />
            </div>
            <div>
              <div className="label-xs mb-1">Wallet</div>
              <input className="input-tech font-mono" required value={form.wallet} onChange={e => setForm({ ...form, wallet: e.target.value })} placeholder="Destination wallet" data-testid="aff-wallet" />
            </div>
            {err && <div className="sm:col-span-2 text-sm text-[#ff3366]">{err}</div>}
            {status && <div className="sm:col-span-2 text-sm text-[#00ff66]">{status}</div>}
            <div className="sm:col-span-2">
              <button className="btn-primary" data-testid="submit-aff-payout">Submit affiliate payout</button>
            </div>
          </form>
        </div>
        <div className="card-tech rounded-lg p-6">
          <div className="label-sm flex items-center gap-2"><Trophy size={14} className="text-[#00ff66]" /> Leaderboard</div>
          <div className="mt-3 space-y-2">
            {board.length === 0 && <p className="text-xs text-zinc-500">No affiliate earnings yet.</p>}
            {board.map(r => (
              <div key={r.rank} className="flex items-center justify-between text-sm" data-testid={`lb-${r.rank}`}>
                <span className="text-zinc-400 font-mono">#{r.rank} · {r.name}</span>
                <span className="font-mono">${formatMoney(r.total_earned)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card-tech rounded-lg p-6">
        <div className="label-sm">Payout requests</div>
        {requests.length === 0 ? <p className="text-sm text-zinc-500 mt-3">No affiliate payout requests yet.</p> : (
          <div className="mt-3 divide-y divide-white/5">
            {requests.map(r => (
              <div key={r.request_id} className="py-3 flex items-center justify-between" data-testid={`aff-req-${r.request_id}`}>
                <div>
                  <div className="font-mono">${formatMoney(r.amount)} <span className="text-zinc-500 text-xs">{r.cryptocurrency?.toUpperCase()} · {r.network}</span></div>
                  <div className="text-xs text-zinc-500 mt-0.5">{new Date(r.created_at).toLocaleString()}</div>
                </div>
                <span className={`chip ${r.status === 'pending' ? 'chip-yellow' : r.status === 'paid' || r.status === 'approved' ? 'chip-green' : 'chip-red'}`}>{r.status.toUpperCase()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ k, v, color }) {
  return (
    <div className="card-tech rounded-lg p-3">
      <div className="label-xs">{k}</div>
      <div className="font-mono text-lg mt-1" style={{ color: color || '#fff' }}>{v}</div>
    </div>
  );
}
function Row({ t, r, note }) {
  return (
    <li className="flex items-center justify-between">
      <span className="text-zinc-400">{t}</span>
      <span className="font-mono">{r} <span className="text-zinc-600 text-xs ml-2">{note}</span></span>
    </li>
  );
}
